import { checkResponse } from "src/components/js/statics1";
import { deleteStepLogMessagesByType, newStepLogMessage } from "../stepLog/stepLogMessageClass";
import { socketOnSave } from "../system/socketClass";

/**
 *
 * @param {number} keikkaId
 * @param {string} varmenne
 * @param {string} token
 * @returns {Promise<boolean>} true if success, false if error
 * @description Saves varmenne to keikka
 *
 */
export async function keikka_varmenne_save_to_db(keikkaId, varmenne, token) {
  const addr = "/api/keikka/saveVarmenneToDB";
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaId,
      varmenne: varmenne,
    }),
  };
  const response = await fetch(import.meta.env.VITE_SERVER_URL + addr, requestOptions);
  if (!(await checkResponse(response).success)) return false;
  return true;
}

/**
 *
 * @param {number} personId
 * @param {string} token
 * @param {number} originId (0 = keikka originates from betoni.online) (obsolete, user sourceAsikasId instead)
 * @param {number} ownerAsiakasId
 * @returns {Promise<number>} 0 if error, new keikkaId if success
 * @description Creates new keikka and returns new keikkaId
 *
 */
export async function keikka_create(
  personId,
  token,
  originId = 0,
  ownerAsiakasId,
  betoniAsiakasId = null,
  pumppuAsiakasId = null,
  sourceAsiakasId = null,
  pumppuAika = null,
  pumppuKesto = 60,
  vehicleId = 0
) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      personId: personId, // person who created the keikka
      originId: originId, // originId is obsolete, use sourceAsiakasId instead, default is 0
      ownerAsiakasId: ownerAsiakasId, // ownerAsiakas of the keikka
      betoniAsiakasId: betoniAsiakasId, // betoniAsiakasId
      pumppuAsiakasId: pumppuAsiakasId, // pumppuAsiakasId
      sourceAsiakasId: sourceAsiakasId, // sourceAsiakasId, where the keikka originates from
      pumppuAika: pumppuAika, // pumppuAika, starttime of the keikka
      pumppuKesto: pumppuKesto, // pumppuKesto, duration of the keikka in minutes
      vehicleId: vehicleId, // vehicleId
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/newKeikka",
    requestOptions
  );
  const cResponse = await checkResponse(response, "keikkaClass.js:createNewKeikka");
  if (!cResponse.success) {
    return false;
  } else {
    return cResponse.data.returnValue;
  }
}

/**
 *
 * @param {number} keikkaId
 * @param {number} creatorPersonId
 * @param {string} token
 * @param {object} socket
 * @returns {number} 0 if error, new keikkaId if success
 * @description Copies keikka and returns new keikkaId
 *
 */
export async function keikka_copy(keikkaId, creatorPersonId, token, socket, ownerAsiakasId = null) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaId,
      creatorPersonId: creatorPersonId,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/copy",
    requestOptions
  );
  const cResponse = await checkResponse(response, "keikkaClass.js:copyKeikka");
  if (!cResponse.success) return 0;
  const result = cResponse.data;
  if (response.status > 399) {
    console.log(result);
    return 0;
  }
  // send socket event TODO:ownerAsiakasId
  const s = new socketOnSave(creatorPersonId, socket, ownerAsiakasId, "keikka", "copy", keikkaId);
  s.comment = "Kopioi tilaus";
  s.function = "keikka_copy";
  s.emitSave();
  return result.returnValue;
}

export async function keikka_search(
  ownerAsiakasId,
  searchString,
  token,
  signal = null,
  usingFullTextSearch = true
) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ownerAsiakasId: ownerAsiakasId,
      searchString: searchString,
      usingFullTextSearch: usingFullTextSearch,
      signal: signal,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/search/",
    requestOptions
  );
  const cResponse = await checkResponse(response, "searchKeikka");
  if (!cResponse.success) {
    console.log(cResponse.message);
    return [];
  }
  const result = cResponse.data;
  return result;
}

export async function keikka_person_add(
  personId,
  keikkaId,
  token,
  socket,
  contactPersonTypeId = 0
) {
  if (!personId || !keikkaId) return false;

  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      personId: personId,
      keikkaId: keikkaId,
      contactPersonTypeId: contactPersonTypeId,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/person/add",
    requestOptions
  );
  const r = await response.json();
  return response.ok && r.rowsAffected[0] > 0;
}

export async function keikka_person_remove(
  personId,
  keikkaId,
  token,
  socket,
  userPersonId = null,
  ownerAsiakasId = null
) {
  if (!keikkaId) return false;
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      personId: personId,
      keikkaId: keikkaId,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/person/remove",
    requestOptions
  );
  if (!response.ok) {
    console.log("Error in keikka_person_remove");
    console.log(response);
    return false;
  }
  // send socket event TODO:ownerAsiakasId, userPersonId
  const s = new socketOnSave(
    userPersonId,
    socket,
    ownerAsiakasId,
    "keikka",
    "removePerson",
    keikkaId
  );
  s.keikka.personId = personId;
  s.comment = "Poista henkilö";
  s.function = "keikka_person_remove";
  s.emitSave();

  return true;
}

export async function keikka_otsikko_save_to_db(keikkaData, token) {
  if (keikkaData.keikkaId === 0) return;
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaData.keikkaId,
      keikkaOtsikko: keikkaData.keikkaOtsikko,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/setOtsikko",
    requestOptions
  ).catch((error) => console.log(error));
  return response.ok;
}

export async function keikka_foreignKey_save_to_db(keikkaData, token) {
  if (keikkaData.keikkaId === 0) return;
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaData.keikkaId,
      foreignKey: keikkaData.foreignKey,
    }),
  };
  fetch(import.meta.env.VITE_SERVER_URL + "/api/keikka/foreignKey/set", requestOptions).catch(
    (error) => console.log(error)
  );
}

export async function keikka_comment_save_to_db(keikkaData, token, socket, personId = null) {
  if (keikkaData.keikkaId === 0) return;
  if (keikkaData.originaluniqueHash === keikkaData.uniqueHash) {
    return;
  }
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaData.keikkaId,
      keikkaComment: keikkaData.keikkaComment,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/comment/set",
    requestOptions
  ).catch((error) => console.log(error));
  if (!(await checkResponse(response).success)) return false;

  // send socket event TODO:personId
  const s = new socketOnSave(
    personId,
    socket,
    keikkaData.ownerAsiakasId,
    "keikkaData",
    "save",
    keikkaData.keikkaId
  );
  s.keikkaData = keikkaData;
  s.comment = "Kommentti";
  s.function = "keikka_comment_save_to_db";
  s.emitSave();

  return true;
}
export async function keikkaVehicle_saveToDB(keikkaId, vehicleId, token) {
  if (!keikkaId || !token) {
    throw new Error("Missing required parameters");
  }

  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "x-access-token": token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        keikkaId,
        vehicleId,
      }),
    };

    const response = await fetch(
      `${import.meta.env.VITE_SERVER_URL}/api/keikka/vehicle/set`,
      requestOptions
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error("Failed to save vehicle to DB:", error);
    throw error; // Re-throw to allow handling by caller
  }
}

// asettaa tiedot onko keikka/tilaus tarjouspyyntö vai ei
export function setIsTarjous(keikkaData, person, _isTarjous, socket) {
  if (_isTarjous) {
    console.log("is tarjouspyyntö");
    const sl = newStepLogMessage("is tarjouspyyntö", person.personId, keikkaData, 27);
    sl.isConfirmed = true;
    sl.requireConfirm = false;
    sl.isSystemMessage = true;
    sl.saveToDB(socket, true);
  } else {
    console.log("is not tarjouspyyntö");
    deleteStepLogMessagesByType(keikkaData.keikkaId, 27, socket);
  }
}

export async function keikka_setIsLocked(keikkaId, isLocked, lockedString, token) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ keikkaId: keikkaId, isLocked: isLocked, lockedString: lockedString }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/locked/set",
    requestOptions
  ).catch((error) => console.log(error));
  const cResponse = await checkResponse(response, "keikkaClass:setIsLocked");
  if (!cResponse.success) return false;
  return true;
}

// Check if two keikkas overlap
export function keikka_doesKeikkaOverlap(keikka1, keikka2) {
  if (keikka1.keikkaId === keikka2.keikkaId) {
    return false;
  }
  const overlap =
    (keikka1.timeStart < keikka2.timeEnd && keikka1.timeStart >= keikka2.timeStart) ||
    (keikka1.timeEnd > keikka2.timeStart && keikka1.timeEnd <= keikka2.timeEnd) ||
    (keikka1.timeStart < keikka2.timeStart && keikka1.timeEnd > keikka2.timeEnd);

  if (keikka1.keikkaId == 4322 || keikka2.keikkaId == 4322) {
    console.log(keikka1);
    console.log(overlap);
  }

  return overlap;
}

export async function keikka_fixTyomaaAsiakas(keikkaID, token) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ keikkaId: keikkaID }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/fixTyomaaAsiakas",
    requestOptions
  ).catch((error) => console.log(error));
  const cResponse = await checkResponse(response, "keikkaClass:fixTyomaaAsiakas");
  if (!cResponse.success) return false;
  return true;
}

export async function keikkaContactPerson_set(keikkaId, personId, token) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ keikkaId: keikkaId, personId: personId }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/contactPerson/set",
    requestOptions
  ).catch((error) => console.log(error));
  return response.ok;
}

export async function saveBetoniToimittajaData(
  keikkaId,
  betoniAsiakasId,
  betoniSijaintiId,
  matkaM,
  matkaAika,
  token,
  varmenne = null
) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaId,
      betoniAsiakasId: betoniAsiakasId,
      betoniSijaintiId: betoniSijaintiId,
      matkaM: matkaM,
      matkaAika: matkaAika,
      varmenne: varmenne,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/betoniToimittaja/set",
    requestOptions
  ).catch((error) => console.log(error));
  return response.ok;
}

export async function savePumppuToimittajaData(
  keikkaId,
  pumppuAsiakasId,
  pumppuSijaintiId,
  matkaM,
  matkaAika,
  token,
  varmenne = null
) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keikkaId: keikkaId,
      pumppuAsiakasId: pumppuAsiakasId,
      pumppuSijaintiId: pumppuSijaintiId,
      matkaM: matkaM,
      matkaAika: matkaAika,
      varmenne: varmenne,
    }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/pumppuToimittaja/set",
    requestOptions
  ).catch((error) => console.log(error));
  return response.ok;
}

export async function keikka_getKeikka(keikkaId, token) {
  const requestOptions = {
    method: "GET",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/grid/keikka/get/" + keikkaId,
    requestOptions
  ).catch((error) => console.log(error));
  return response.json();
}
export async function keikka_setSourceAsiakasId(keikkaId, sourceAsiakasId, token) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ keikkaId: keikkaId, sourceAsiakasId: sourceAsiakasId }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/sourceAsiakas/set",
    requestOptions
  ).catch((error) => console.log(error));
  return response.ok;
}
export async function keikkaList_forPumppari(personId, startTime, endTime, token) {
  const requestOptions = {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ personId: personId, startTime: startTime, endTime: endTime }),
  };
  const response = await fetch(
    import.meta.env.VITE_SERVER_URL + "/api/keikka/getKeikkaListForPumppari/" + personId,
    requestOptions
  ).catch((error) => console.log(error));
  return response.json();
}
